import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import axios from "axios";

import Panoramica from "./Panoramica/Panoramica";
import CostiCarburante from "./CostiCarburante/CostiCarburante";
import DettaglioCosti from "./DettaglioCosti/DettaglioCosti";
import FiltroCategorie from "../../components/FiltroCategorie/FiltroCategorie";
import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";
import { MOCKED_TRANSAUTOM_CATEGORIES } from "../../mocks/MockedCategories";

import "../OperazioniHr/OperazioniHr.css";

const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="oper-hr-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

// Formatta una data in YYYY-MM-DD
const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const monthToFirstDay = (monthValue) => {
    if (!monthValue) return "";
    return `${monthValue}-01`;
};

const monthToLastDay = (monthValue) => {
    if (!monthValue) return "";
    const [year, month] = monthValue.split("-").map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthValue}-${String(lastDay).padStart(2, "0")}`;
};

const dateToMonth = (dateValue) => {
    if (!dateValue) return "";
    return dateValue.substring(0, 7);
};

const getDefaultDateRange = () => {
    const today = new Date();
    const lastDayCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDay12MonthsAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    return {
        from: formatDate(firstDay12MonthsAgo),
        to: formatDate(lastDayCurrentMonth),
    }
};

const defaultDates = getDefaultDateRange();

const TransAutomezzi = () => {
    const navigate = useNavigate();

    // stati per la UI
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [loading, setLoading] = useState(false);

    // cache per evitare reload
    const dataCache = useRef({
        filters: null,
        data: null
    });

    // stati per i filtri
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [selectedSettore, setSelectedSettore] = useState([]);
    const [selectedCommessa, setSelectedCommessa] = useState([]);
    const [selectedTarga, setSelectedTarga] = useState([]);
    const [selectedProdotto, setSelectedProdotto] = useState([]);
    const [selectedDistributore, setSelectedDistributore] = useState([]);
    const [selectedCantiere, setSelectedCantiere] = useState([]);
    const [selectedTipologia, setSelectedTipologia] = useState([]);

    //filtri
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: defaultDates.from,
        toDate: defaultDates.to,
        settore: [],
        commessa: [],
        targa: [],
        prodotto: [],
        distributore: [],
        cantiere: [],
        tipologia: []
    });

    const [filterOptions, setFilterOptions] = useState({
        settore: [],
        commessa: [],
        targa: [],
        prodotto: [],
        distributore: [],
        cantiere: [],
        tipologia: []
    });

    const handleFromMonthChange = (e) => {
        const monthValue = e.target.value;
        const firstDay = monthToFirstDay(monthValue);
        setFromDate(firstDay);
    };

    const handleToMonthChange = (e) => {
        const monthValue = e.target.value;
        const lastDay = monthToLastDay(monthValue);
        setToDate(lastDay);
    };

    useEffect(() => {
        const fetchFilters = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/trans-automezzi/filters`);
                setFilterOptions(prev => ({
                    ...prev,
                    settore: res.data.settore.map(s => ({ value: s.id, label: s.valore })),
                    commessa: res.data.commessa.map(c => ({ value: c.id, label: c.valore })),
                    targa: res.data.targa.map(t => ({ value: t.valore, label: t.valore })),
                    prodotto: res.data.prodotto.map(p => ({ value: p.valore, label: p.valore })),
                    distributore: res.data.distributore.map(d => ({ value: d.valore, label: d.valore })),
                    cantiere: res.data.cantiere.map(ca => ({ value: ca.valore, label: ca.valore })),
                    tipologia: res.data.tipologia.map(ti => ({ value: ti.valore, label: ti.valore }))
                }));
            } catch (error) {
                console.error("Errore nel caricamento dei filtri:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchFilters();
    }, [API_URL]);

    const handleApply = useCallback(() => {
        setAppliedFilters({
            fromDate,
            toDate,
            settore: selectedSettore,
            commessa: selectedCommessa,
            targa: selectedTarga,
            prodotto: selectedProdotto,
            distributore: selectedDistributore,
            cantiere: selectedCantiere,
            tipologia: selectedTipologia
        });
    }, [fromDate, toDate, selectedSettore, selectedCommessa, selectedTarga, selectedProdotto, selectedDistributore, selectedCantiere, selectedTipologia]);

    const handleReset = useCallback(() => {
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setSelectedSettore([]);
        setSelectedCommessa([]);
        setSelectedTarga([]);
        setSelectedProdotto([]);
        setSelectedDistributore([]);
        setSelectedCantiere([]);
        setSelectedTipologia([]);
        setAppliedFilters({
            fromDate: defaultDates.from,
            toDate: defaultDates.to,
            settore: [],
            commessa: [],
            targa: [],
            prodotto: [],
            distributore: [],
            cantiere: [],
            tipologia: []
        });
    }, []);

    return (
        <div className="operazioni-hr-page">
            <header className="oper-hr-header">
                <div className="oper-hr-header-content">
                    <button className="oper-hr-back-btn" onClick={() => navigate('/reportistica')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Indietro
                    </button>

                    <div className="oper-hr-header-title">
                        <div className="oper-hr-title-row">
                            <h1>Trans. Automezzi</h1>
                            <button className="oper-hr-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                                <span>i</span>
                            </button>
                        </div>
                        <p className="oper-hr-subtitle">Visualizzazione transazioni automezzi e costi carburante</p>
                    </div>
                </div>
            </header>

            {showInfoModal && (
                <div className="oper-hr-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="oper-hr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="oper-hr-modal-header">
                            <div className="oper-hr-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="oper-hr-modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="oper-hr-modal-body">
                            <div className="oper-hr-modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina permette di analizzare nel dettaglio le transazioni di rifornimento
                                    della flotta automezzi. Consente di monitorare volumi, costi del carburante e
                                    costo al litro per ogni veicolo, distributore e prodotto, individuando
                                    anomalie di consumo e ottimizzando la gestione dei costi operativi.
                                </p>
                            </div>
                            <div className="oper-hr-modal-section">
                                <h4>Come utilizzarla?</h4>
                                <ul>
                                    <li><strong>Filtri:</strong> Applica filtri per periodo (mese inizio/fine), targa, prodotto, distributore, cantiere e tipologia veicolo. In Costi Carburante sono disponibili anche i filtri per settore e commessa</li>
                                    <li><strong>Panoramica:</strong> Visualizza grafici interattivi con la distribuzione dei costi per prodotto e gestore, l'andamento del costo medio al litro nel tempo, volumi e costi totali per prodotto e il confronto per singola targa. Cliccando sui grafici puoi filtrare i dati in modo dinamico</li>
                                    <li><strong>Costi Carburante:</strong> Analizza i costi tramite una tabella parametrica configurabile per righe (cantiere, sottotipologia, targa, settore, commessa, attivita) e colonne (anno, trimestre, mese), con indicatori su volume totale, costo al litro, km percorsi e km per litro</li>
                                    <li><strong>Dettaglio Costi:</strong> Consulta l'elenco completo delle singole transazioni di rifornimento con data, targa, prodotto, volume, costo, distributore e posizione. I dati sono ordinabili e paginabili, con possibilita di esportazione in Excel</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="oper-hr-main">
                <div className="oper-hr-actions">
                    <button
                        type="button"
                        className={`oper-hr-action-btn ${showFiltersPanel ? 'active' : ''}`}
                        onClick={() => setShowFiltersPanel(prev => !prev)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        <span>{showFiltersPanel ? 'Nascondi Filtri' : 'Mostra Filtri'}</span>
                    </button>
                </div>

                {showFiltersPanel && (
                    <div className="oper-hr-panel">
                        <div className="oper-hr-filters-grid">
                            <div className="oper-hr-filter-item">
                                <label>Mese Inizio</label>
                                <MonthPickerModal
                                    value={dateToMonth(fromDate)}
                                    onChange={handleFromMonthChange}
                                    max={dateToMonth(toDate) || undefined}
                                    label="Mese Inizio"
                                />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Mese Fine</label>
                                <MonthPickerModal
                                    value={dateToMonth(toDate)}
                                    onChange={handleToMonthChange}
                                    min={dateToMonth(fromDate) || undefined}
                                    label="Mese Fine"
                                />
                            </div>
                            {selectedCategory === "Costi Carburante" && (
                                <div className="oper-hr-filter-item">
                                    <label>Settore</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedSettore}
                                        options={filterOptions.settore}
                                        onChange={setSelectedSettore}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            )}
                            {selectedCategory === "Costi Carburante" && (
                                <div className="oper-hr-filter-item">
                                    <label>Commessa</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutte"
                                        value={selectedCommessa}
                                        options={filterOptions.commessa}
                                        onChange={setSelectedCommessa}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            )}
                            <div className="oper-hr-filter-item">
                                <label>Targa</label>
                                <Select
                                    classNamePrefix="oper-hr-select"
                                    placeholder="Tutti"
                                    value={selectedTarga}
                                    options={filterOptions.targa}
                                    onChange={setSelectedTarga}
                                    isMulti
                                    components={{ MultiValue: MultiValueContainer }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                            {(selectedCategory === "Dettaglio Costi" || selectedCategory === "Panoramica") && (
                                <div className="oper-hr-filter-item">
                                    <label>Prodotto</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedProdotto}
                                        options={filterOptions.prodotto}
                                        onChange={setSelectedProdotto}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            )}
                            {(selectedCategory === "Dettaglio Costi" || selectedCategory === "Panoramica") && (         
                                <div className="oper-hr-filter-item">
                                    <label>Distributore</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedDistributore}
                                        options={filterOptions.distributore}
                                        onChange={setSelectedDistributore}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            )}
                            <div className="oper-hr-filter-item">
                                <label>Cantiere</label>
                                <Select
                                    classNamePrefix="oper-hr-select"
                                    placeholder="Tutti"
                                    value={selectedCantiere}
                                    options={filterOptions.cantiere}
                                    onChange={setSelectedCantiere}
                                    isMulti
                                    components={{ MultiValue: MultiValueContainer }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Tipologia</label>
                                <Select
                                    classNamePrefix="oper-hr-select"
                                    placeholder="Tutti"
                                    value={selectedTipologia}
                                    options={filterOptions.tipologia}
                                    onChange={setSelectedTipologia}
                                    isMulti
                                    components={{ MultiValue: MultiValueContainer }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                            <div className="oper-hr-filter-actions">
                                <button type="button" className="oper-hr-btn-primary" onClick={handleApply}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Applica
                                </button>
                                <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="oper-hr-data-section">
                    <FiltroCategorie
                        categories={MOCKED_TRANSAUTOM_CATEGORIES}
                        filteredCategory={setSelectedCategory}
                        customCss={"reportistica-section"}
                    />
                    {selectedCategory === "Panoramica" && (
                        <Panoramica
                            filters={appliedFilters}
                            loading={loading}
                        />
                    )}
                    {selectedCategory === "Dettaglio Costi" && (
                        <DettaglioCosti
                            filters={appliedFilters}
                            loading={loading}
                        />
                    )}
                    {selectedCategory === "Costi Carburante" && (
                        <CostiCarburante
                            filters={appliedFilters}
                            loading={loading}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};

export default TransAutomezzi;



